import type { CookieSerializeOptions } from 'cookie-es';
import { computed } from 'vue';
import type ConsentLevel from '../components/common/common-consent/consent-level';
import { createSharedComposable } from '@vueuse/core';
import { useNloRequestURL } from './useNloRequestURL';

/**
 * Composable that exposes Nlo portal cookies
 */
const useInternalNloPortalCookie = () => {
	const portalBaseUrl = useNloPortalBaseUrl();
	const requestUrl = useNloRequestURL();
	/**
	 * NloRequestURL is based on commonBaseUrl which is wrong for whitelabel.
	 * nuxtRequestUrl only used on dev environments
	 */
	const nuxtRequestUrl = useRequestURL();

	/**
	 * The cookie domain used for cookies that are not brand specific.
	 */
	const portalDomain = computed(() => {
		if (portalBaseUrl.includes('localhost')) {
			// Local dev stuff
			return 'localhost';
		}

		const portalDomainURLObject = new URL(portalBaseUrl);
		return portalDomainURLObject.hostname // "example.com" or "whitelabel.example.com"
			.split('.') // Split by dots into ["example", "com"] or ["whitelabel", "example", "com"]
			.slice(-2) // Take the last two parts, which are the domain and TLD ["example", "com"] or ["example", "com"] (white label is gone)
			.join('.'); // Join them back together to form "example.com" or "example.com"
	});

	const isHttps = computed(() => requestUrl.protocol === 'https:');
	const isLocalHost = computed(() => portalDomain.value.includes('localhost'));

	const portalCookieOptions = computed((): Omit<CookieSerializeOptions, 'encode'> => {
		const cookieDomain = isLocalHost.value ? portalDomain.value : '.' + portalDomain.value;

		return {
			domain: cookieDomain,
			secure: isHttps.value ? true : undefined, // Only insecure locally, unless someone has a better solution with https testing.
		};
	});

	const cookieOptions = computed((): Omit<CookieSerializeOptions, 'encode'> => {
		const cookieDomain = isLocalHost.value ? nuxtRequestUrl.hostname : '.' + portalDomain.value;

		return {
			domain: cookieDomain,
			secure: isHttps.value ? true : undefined, // Only insecure locally, unless someone has a better solution with https testing.
		};
	});

	const cookiePrefixes = {
		secure: '__Secure-', // Secure cross-subdomain cookies
		host: '__Host-', // Secure same-domain cookies (exclude subdomains)
	};

	const cookies = {
		/**
		 * Authentication cookie that is present when a user is logged in.
		 * Please note that this cookie can be expired, so this is not a guarantee that the user still has an active session.
		 */
		authenticationCookie: useCookie(cookiePrefixes.secure + 'nlportal.loggedin', portalCookieOptions.value),

		/**
		 * The hideFooter cookie is used to hide the portal footer.
		 * This is usually done by adding ?hideFooter=true to the URL. The portal header will read this param and set the cookie.
		 */
		hideFooter: useCookie<boolean | undefined>(cookiePrefixes.secure + 'nlportal.hideFooter', portalCookieOptions.value),

		/**
		 * The hideHeader cookie is used to hide the portal header.
		 * This is usually done by adding ?hideHeader=true to the URL. The portal header will read this param and set the cookie.
		 */
		hideHeader: useCookie<boolean | undefined>(cookiePrefixes.secure + 'nlportal.hideHeader', portalCookieOptions.value),

		/**
		 * The emergency banner cookie holds the dismissed emergency banner id.
		 * The is will prevent the banner from showing again.
		 */
		emergencyBannerDismissed: useCookie<string[] | undefined>('nlportal.emergency', portalCookieOptions.value),

		/**
		 * Flash message cookies set by the .NET server.
		 * These are used in cases where the user comes from an old-setup page or from a payment/login/registration flow
		 */

		flashMessageMessage: useCookie(cookiePrefixes.secure + 'nlportal.flashmessage_message', portalCookieOptions.value),
		flashMessageType: useCookie(cookiePrefixes.secure + 'nlportal.flashmessage_type', portalCookieOptions.value),
		flashMessageAutoHide: useCookie<boolean | undefined>(
			cookiePrefixes.secure + 'nlportal.flashmessage_auto_hide',
			portalCookieOptions.value,
		),

		/**
		 * Is set when the registration is completed successfully.
		 * The cookie will determine if the registration successfull modal should be shown.
		 */
		registered: useCookie<boolean | undefined>(cookiePrefixes.secure + 'nlportal.registered', portalCookieOptions.value),

		/**
		 * The consent cookie that holds the user's consent level.
		 */
		cookieConsent: useCookie<ConsentLevel | undefined>('cookieConsent', {
			...cookieOptions.value,
			maxAge: 365 * 24 * 60 * 60,
		}),
	};

	return {
		...cookies,
		/**
		 * Utility functions to mutate cookies
		 */
		utils: {
			/**
			 * Clears the flash message cookies.
			 */
			clearFlashMessage: () => {
				cookies.flashMessageMessage.value = undefined;
				cookies.flashMessageType.value = undefined;
				cookies.flashMessageAutoHide.value = undefined;
			},
			/**
			 * Clears the account registered message cookies.
			 */
			clearRegistered: () => {
				cookies.registered.value = undefined;
			},
			/**
			 * Sets the hide Header cookies
			 */
			hideHeader: (hide: boolean) => {
				cookies.hideHeader.value = hide;
			},
			/**
			 * Sets the hide Footer cookies
			 */
			hideFooter: (hide: boolean) => {
				cookies.hideFooter.value = hide;
			},
		},
	};
};

const useNloPortalCookie = createSharedComposable(useInternalNloPortalCookie);

export default useNloPortalCookie;
